'use client';

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ApiError, extractData, type ExtractRunSession } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { toUserFacingExtractionErrorMessage } from '@/lib/extractionErrorMessage';
import { getTurnstileErrorMessage } from '@/lib/authCopy';
import Link from 'next/link';
import { DeferredTurnstile, type BoundTurnstileObject } from '@/components/DeferredTurnstile';
import {
  isClientTurnstileBypassEnabled,
  isCloudflareTestingSiteKey,
  resolvePublicTurnstileSiteKey,
} from '@/lib/turnstileConfig';

interface ExtractFormProps {
  onExtractStart: (run: ExtractRunSession | null) => void;
  onExtractFailed: (message?: string) => void;
  isLoading: boolean;
  extractionComplete: boolean;
  turnstileSiteKeyProp?: string | null;
  turnstileBypassProp?: boolean;
}

type ExtractMode = 'simple' | 'css' | 'xpath' | 'json';

const DEFAULT_EXTRACTION_URL =
  'https://www.ozon.ru/product/aerogril-boma-10-v-1-aerogril-elektricheskiy-8-l-1800-vt-12-programm-2241802245/';

export function ExtractForm({
  onExtractStart,
  onExtractFailed,
  isLoading,
  extractionComplete,
  turnstileSiteKeyProp,
  turnstileBypassProp,
}: ExtractFormProps) {
  const [url, setUrl] = useState<string>(DEFAULT_EXTRACTION_URL);
  const [request, setRequest] = useState<string>('технические характеристики');
  const [formError, setFormError] = useState<string | null>(null);
  const [mode, setMode] = useState<ExtractMode>('simple');
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionGuardRef = useRef(false);
  const turnstileRef = useRef<BoundTurnstileObject | null>(null);
  const captchaResolveRef = useRef<((token: string) => void) | null>(null);
  const captchaRejectRef = useRef<((err: Error) => void) | null>(null);
  const captchaTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const captchaReadyWaitersRef = useRef<{ resolve: (v: BoundTurnstileObject) => void; reject: (e: Error) => void; timeout: ReturnType<typeof setTimeout> } | null>(null);
  const captchaTokenRef = useRef<string | null>(null);
  const captchaExecutionRef = useRef(false);
  // Prefer the page-provided site key and fall back to the shared env/test-key resolver.
  const resolvedPropKey = turnstileSiteKeyProp?.trim();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { toast } = useToast();
  const turnstileSiteKey =
    (resolvedPropKey && resolvedPropKey.length > 0 ? resolvedPropKey : null) ??
    resolvePublicTurnstileSiteKey({
      envValue: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
      nodeEnv: process.env.NODE_ENV,
    });

  const turnstileBypass = typeof turnstileBypassProp === 'boolean'
    ? turnstileBypassProp
    : isClientTurnstileBypassEnabled(process.env.NEXT_PUBLIC_TURNSTILE_BYPASS);
  const turnstileEnabled = !isAuthenticated && !isAuthLoading && Boolean(turnstileSiteKey) && !turnstileBypass;
  const usesAlwaysPassKey = isCloudflareTestingSiteKey(turnstileSiteKey);

  const resetFormLocks = () => {
    submissionGuardRef.current = false;
    setIsSubmitting(false);
  };

  const clearCaptchaWaiters = () => {
    if (captchaTimeoutRef.current) {
      clearTimeout(captchaTimeoutRef.current);
      captchaTimeoutRef.current = null;
    }
    captchaResolveRef.current = null;
    captchaRejectRef.current = null;
  };

  const clearCaptchaExecution = () => {
    captchaExecutionRef.current = false;
  };

  const waitForCaptchaReady = async () => {
    if (!turnstileSiteKey) {
      throw new Error('Captcha not configured');
    }
    if (turnstileRef.current) return turnstileRef.current;

    return new Promise<BoundTurnstileObject>((resolve, reject) => {
      if (captchaReadyWaitersRef.current) {
        clearTimeout(captchaReadyWaitersRef.current.timeout);
      }

      const timeout = setTimeout(() => {
        captchaReadyWaitersRef.current = null;
        reject(new Error('Captcha not ready'));
      }, 15000);

      captchaReadyWaitersRef.current = { resolve, reject, timeout };
    });
  };

  const executeCaptcha = async () => {
    const boundTurnstile = await waitForCaptchaReady();

    if (usesAlwaysPassKey || captchaTokenRef.current || captchaExecutionRef.current) {
      return;
    }

    captchaExecutionRef.current = true;
    try {
      boundTurnstile.execute();
    } catch (error) {
      clearCaptchaExecution();
      throw error instanceof Error ? error : new Error('Captcha failed');
    }
  };

  const requestCaptchaToken = async () => {
    if (captchaTokenRef.current) {
      return captchaTokenRef.current;
    }

    const boundTurnstile = await waitForCaptchaReady();

    return new Promise<string>((resolve, reject) => {
      clearCaptchaWaiters();
      captchaTimeoutRef.current = setTimeout(() => {
        clearCaptchaWaiters();
        clearCaptchaExecution();
        reject(new Error('Captcha timed out'));
      }, 8000);

      captchaResolveRef.current = (token: string) => {
        clearCaptchaWaiters();
        resolve(token);
      };
      captchaRejectRef.current = (err: Error) => {
        clearCaptchaWaiters();
        reject(err);
      };

      try {
        if (captchaTokenRef.current) {
          const existingToken = captchaTokenRef.current;
          clearCaptchaWaiters();
          resolve(existingToken);
          return;
        }

        if (!usesAlwaysPassKey && !captchaExecutionRef.current) {
          captchaExecutionRef.current = true;
          boundTurnstile.execute();
        }
      } catch (error) {
        clearCaptchaWaiters();
        clearCaptchaExecution();
        reject(error instanceof Error ? error : new Error('Captcha failed'));
      }
    });
  };

  const handleCaptchaVerify = (token: string) => {
    captchaTokenRef.current = token;
    clearCaptchaExecution();
    captchaResolveRef.current?.(token);
    if (captchaReadyWaitersRef.current) {
      clearTimeout(captchaReadyWaitersRef.current.timeout);
      captchaReadyWaitersRef.current.resolve(turnstileRef.current as BoundTurnstileObject);
      captchaReadyWaitersRef.current = null;
    }
  };

  const handleCaptchaError = (error?: unknown) => {
    captchaTokenRef.current = null;
    clearCaptchaExecution();
    const err = error instanceof Error ? error : new Error('Captcha failed');
    captchaRejectRef.current?.(err);
    if (captchaReadyWaitersRef.current) {
      clearTimeout(captchaReadyWaitersRef.current.timeout);
      captchaReadyWaitersRef.current.reject(err);
      captchaReadyWaitersRef.current = null;
    }
  };

  useEffect(() => {
    captchaTokenRef.current = null;
    clearCaptchaWaiters();
    clearCaptchaExecution();

    if (!turnstileEnabled || usesAlwaysPassKey) {
      return;
    }

    void executeCaptcha().catch((error) => {
      console.warn('[extract-form] Captcha warmup failed', error);
    });
  }, [turnstileEnabled, usesAlwaysPassKey]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionGuardRef.current || isSubmitting) {
      return;
    }

    if (isAuthLoading) {
      setFormError('Подождите, пока мы подтверждаем вашу сессию...');
      resetFormLocks();
      return;
    }

    setFormError(null);

    if (!url.trim()) {
      setFormError('URL обязателен');
      resetFormLocks();
      return;
    }

    if (mode === 'simple' && !request.trim()) {
      setFormError('Запрос обязателен');
      resetFormLocks();
      return;
    }

    submissionGuardRef.current = true;

    // Set loading state immediately to prevent multiple clicks
    setIsSubmitting(true);
    onExtractStart(null);

    // Create the extraction request using the new contract (prompt + options)
    const extractionRequest = {
      url: url.trim(),
      prompt: request.trim(),
      options: {
        screenshot: includeScreenshot,
      },
    } as const;

    let captchaTokenToSend: string | undefined;
    const captchaRequired = !isAuthenticated && !isAuthLoading && !turnstileBypass;
    const captchaAvailable = Boolean(turnstileSiteKey);

    if (captchaRequired && !captchaAvailable) {
      const userMessage = 'Гостевое извлечение сейчас недоступно. Войдите в аккаунт или попробуйте позже.';
      console.error('[extract-form] Captcha required but no site key is configured (prod blocks guest flow).');
      toast({
        variant: 'destructive',
        title: 'Извлечение недоступно',
        description: userMessage,
      });
      onExtractFailed(userMessage);
      resetFormLocks();
      return;
    }

    if (captchaRequired) {
      try {
        captchaTokenToSend = await requestCaptchaToken();
      } catch (err) {
        console.error('Captcha error:', err);
        const userMessage = getTurnstileErrorMessage(err);
        toast({
          variant: 'destructive',
          title: 'Не удалось запустить извлечение',
          description: userMessage,
        });
        onExtractFailed(userMessage);
        turnstileRef.current?.reset();
        captchaTokenRef.current = null;
        resetFormLocks();
        return;
      }
    }

    try {
      const response = await extractData({
        ...extractionRequest,
        captchaToken: turnstileBypass ? 'bypass' : captchaTokenToSend,
      });

      onExtractStart(response);

      // Show toast notification
      toast({
        title: 'Извлечение запущено',
        description: 'Обрабатываем ваш запрос...',
      });
    } catch (err) {
      console.error("Extraction error:", err);

      let userMessage = 'Не удалось извлечь данные. Попробуйте еще раз.';

      if (err instanceof ApiError) {
        if (err.status === 401) {
          userMessage = 'Войдите в аккаунт, чтобы запустить извлечение.';
        } else if (err.status === 429) {
          userMessage = 'Создайте бесплатный аккаунт, чтобы продолжить.';
        } else if (err.message) {
          userMessage = toUserFacingExtractionErrorMessage(err.message);
        }
      }

      if (!turnstileBypass && err instanceof ApiError && (err.status === 400 || err.status === 429)) {
        turnstileRef.current?.reset();
        captchaTokenRef.current = null;
      }

      // Show error toast
      toast({
        variant: 'destructive',
        title: 'Не удалось извлечь данные',
        description: userMessage,
      });
      
      onExtractFailed(userMessage);
    } finally {
      setIsSubmitting(false);
      submissionGuardRef.current = false;
    }
  };

  return (
    <div className="space-y-5">
      <form
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        <div className="space-y-2">
          <label
            htmlFor="url"
            className="block text-xs font-semibold uppercase tracking-[0.18em] text-[var(--site-ink-subtle)]"
          >
            Адрес сайта
          </label>
          <Input
            id="url"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
          />
        </div>

        <div className="space-y-5 rounded-[1.6rem] border border-[color:var(--site-border-soft)] bg-[var(--site-surface-deep)] p-[var(--site-home-form-group-pad)] max-lg:space-y-4 max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:p-0">
          <div className="grid grid-cols-2 gap-2 rounded-[1.2rem] border border-[color:var(--site-border-soft)] bg-white/75 p-[var(--site-home-mode-tabs-pad)] max-lg:border-transparent max-lg:bg-[var(--site-surface-inset)] lg:grid-cols-4">
            {[
              { value: 'simple', label: 'Простой' },
              { value: 'css', label: 'CSS-селектор' },
              { value: 'xpath', label: 'XPath' },
              { value: 'json', label: 'JSON-шаблон' },
            ].map((option) => {
              const locked = option.value !== 'simple' && !isAuthenticated;
              const active = mode === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`min-h-11 rounded-[1rem] px-[var(--site-home-mode-trigger-px)] py-[var(--site-home-mode-trigger-py)] text-[length:var(--site-home-mode-trigger-font-size)] font-medium transition-colors ${
                    active
                      ? 'bg-[var(--site-primary-fill)] text-[var(--site-primary-text)] shadow-[var(--site-shadow-button)]'
                      : 'text-[var(--site-ink-muted)] hover:bg-[var(--site-surface-inset)] hover:text-[var(--site-ink-strong)]'
                  } ${locked ? 'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-[var(--site-ink-muted)]' : ''}`}
                  onClick={() => !locked && setMode(option.value as ExtractMode)}
                  disabled={locked}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <div className="space-y-2">
            {mode === 'simple' && (
              <div className="space-y-2">
                <label
                  htmlFor="request"
                  className="block text-xs font-semibold uppercase tracking-[0.18em] text-[var(--site-ink-subtle)]"
                >
                  Какие данные вы хотите извлечь?
                </label>
                <Textarea
                  id="request"
                  placeholder="например, характеристики товара, цена и т.д."
                  value={request}
                  onChange={(e) => setRequest(e.target.value)}
                  className="min-h-[var(--site-textarea-min-h)]"
                  required
                />
              </div>
            )}
          </div>
        </div>

        {formError && (
          <div className="rounded-[1.2rem] border border-[color:rgba(239,68,68,0.22)] bg-[color:rgba(254,242,242,0.92)] px-4 py-3 text-sm text-red-600">
            {formError}
          </div>
        )}

        {turnstileEnabled && (
          <div className="space-y-2">
            <p className="text-xs leading-5 text-[var(--site-ink-subtle)]">
              Антиспам-проверка запускается автоматически. Если потребуется подтверждение, виджет появится здесь.
            </p>
            <div className="flex min-h-[1px] justify-end">
              <DeferredTurnstile
                sitekey={turnstileSiteKey as string}
                size="flexible"
                appearance={usesAlwaysPassKey ? 'always' : 'interaction-only'}
                execution={usesAlwaysPassKey ? undefined : 'execute'}
                onLoad={(_id, bound) => {
                  turnstileRef.current = bound;
                  if (captchaReadyWaitersRef.current) {
                    clearTimeout(captchaReadyWaitersRef.current.timeout);
                    captchaReadyWaitersRef.current.resolve(bound);
                    captchaReadyWaitersRef.current = null;
                  }
                  if (!usesAlwaysPassKey) {
                    void executeCaptcha().catch((error) => {
                      console.warn('[extract-form] Captcha execute on load failed', error);
                    });
                  }
                }}
                onVerify={handleCaptchaVerify}
                onError={handleCaptchaError}
                onExpire={() => handleCaptchaError(new Error('Captcha expired'))}
                onTimeout={() => handleCaptchaError(new Error('Captcha timed out'))}
              />
            </div>
          </div>
        )}

        {!isAuthenticated && mode !== 'simple' && (
          <div className="rounded-[1.2rem] border border-[color:rgba(245,158,11,0.22)] bg-[color:rgba(255,251,235,0.94)] px-4 py-3 text-amber-700">
            <p className="text-sm">
              <strong>Доступно в аккаунте:</strong>{' '}
              <Link href="/login" className="text-[var(--site-accent-text)] hover:underline">
                войдите
              </Link>{' '}
              или{' '}
              <Link href="/signup" className="text-[var(--site-accent-text)] hover:underline">
                создайте аккаунт
              </Link>{' '}
              чтобы открыть расширенные режимы извлечения.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-4 rounded-[1.6rem] border border-[color:var(--site-border-soft)] bg-[var(--site-surface-deep)] p-[var(--site-home-form-group-pad)] sm:flex-row sm:items-center sm:justify-between max-lg:rounded-none max-lg:border-x-0 max-lg:border-b-0 max-lg:bg-transparent max-lg:px-0 max-lg:pb-0 max-lg:pt-4">
          <div className="flex items-center space-x-2">
            <label className="inline-flex cursor-pointer items-center">
              <span className="relative inline-flex h-11 w-11 items-center">
                <input
                  type="checkbox"
                  checked={includeScreenshot}
                  onChange={() => setIncludeScreenshot(!includeScreenshot)}
                  className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                />
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-flex h-6 w-11 items-center rounded-full transition-colors ${includeScreenshot ? 'bg-[var(--site-primary-fill)]' : 'bg-[color:rgba(23,38,59,0.14)]'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${includeScreenshot ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </span>
              </span>
              <span className="ml-2 text-sm font-medium text-[var(--site-ink-strong)]">Добавить скриншот</span>
            </label>
          </div>

          <Button
            type="submit"
            size="lg"
            className="min-w-[9.5rem] px-[var(--site-button-px-lg)] text-[length:var(--site-input-font-size)]"
            disabled={isLoading || isSubmitting || !extractionComplete || isAuthLoading}
          >
            {isSubmitting ? 'Извлекаем...' : isLoading ? 'Обрабатываем...' : 'Извлечь'}
          </Button>
        </div>
      </form>
    </div>
  );
}
